'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { cn } from '../utils/cn';

interface VideoPlayerProps {
  src: string;
  poster?: string | null;
  className?: string;
  videoClassName?: string;
  autoPlay?: boolean;
  loop?: boolean;
  preload?: 'auto' | 'metadata' | 'none';
  onLoadedMetadata?: () => void;
  onLoadedData?: () => void;
  onCanPlay?: () => void;
  onError?: () => void;
}

/**
 * Custom video player without native controls.
 */
export function VideoPlayer({
  src,
  poster,
  className,
  videoClassName,
  autoPlay = false,
  loop = false,
  preload = 'auto',
  onLoadedMetadata,
  onLoadedData,
  onCanPlay,
  onError,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(false);
  const [showInitialPlay, setShowInitialPlay] = useState(!autoPlay);
  const [tapIcon, setTapIcon] = useState<'play' | 'pause' | null>(null);
  const tapIconKey = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (showInitialPlay) {
      setShowInitialPlay(false);
      video.play()
        .then(() => setIsPlaying(true))
        .catch(() => setShowInitialPlay(true));
      return;
    }

    if (video.paused) {
      video.play()
        .then(() => setIsPlaying(true))
        .catch(() => setShowInitialPlay(true));
      setTapIcon('play');
    } else {
      video.pause();
      setIsPlaying(false);
      setTapIcon('pause');
    }

    tapIconKey.current += 1;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setTapIcon(null), 600);
  }, [showInitialPlay]);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (!autoPlay) {
      video.pause();
      if (video.currentTime > 0) {
        video.currentTime = 0;
      }
      setIsPlaying(false);
      setShowInitialPlay(true);
      return;
    }

    const tryPlay = () => {
      video.play().catch(() => {
        setShowInitialPlay(true);
      });
    };

    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.addEventListener('canplay', tryPlay, { once: true });
      return () => video.removeEventListener('canplay', tryPlay);
    }
  }, [autoPlay, src]);

  useEffect(() => (
    () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  ), []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setShowInitialPlay(false);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  return (
    <div className={cn('relative overflow-hidden', className)} onClick={togglePlay}>
      <video
        ref={videoRef}
        src={src}
        poster={poster || undefined}
        muted={isMuted}
        playsInline
        autoPlay={autoPlay}
        loop={loop}
        preload={preload}
        className={cn('h-full w-full', videoClassName || 'object-cover')}
        onLoadedMetadata={onLoadedMetadata}
        onLoadedData={onLoadedData}
        onCanPlay={onCanPlay}
        onError={onError}
        onPlay={handlePlay}
        onPause={handlePause}
      />

      {showInitialPlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-md">
            <Play className="ml-1 h-8 w-8 text-white" fill="white" />
          </div>
        </div>
      )}

      {tapIcon && (
        <div
          key={tapIconKey.current}
          className="video-player-tap-icon pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm">
            {tapIcon === 'play' ? (
              <Play className="ml-0.5 h-7 w-7 text-white" fill="white" />
            ) : (
              <Pause className="h-7 w-7 text-white" fill="white" />
            )}
          </div>
        </div>
      )}

      {!showInitialPlay && (
        <button
          onClick={toggleMute}
          className="absolute bottom-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm transition-transform active:scale-90"
        >
          {isMuted ? (
            <VolumeX className="h-5 w-5 text-white" />
          ) : (
            <Volume2 className="h-5 w-5 text-white" />
          )}
        </button>
      )}
    </div>
  );
}
